import React, { useEffect, useState } from 'react';
import { syncSettingsAPI } from '../../services/api';

interface SyncSettings {
  sync_interval_hours: number;
  auto_sync_enabled: boolean;
  last_auto_sync_time: string | null;
}

export const SyncSettings: React.FC = () => {
  const [settings, setSettings] = useState<SyncSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Intervalles prédéfinis
  const intervalOptions = [1, 2, 3, 4, 6, 12, 24];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await syncSettingsAPI.getSettings();
      setSettings(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleIntervalChange = async (newInterval: number) => {
    if (!settings) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await syncSettingsAPI.updateSettings({
        sync_interval_hours: newInterval,
        auto_sync_enabled: settings.auto_sync_enabled,
      });

      setSettings({
        ...settings,
        sync_interval_hours: newInterval,
      });

      setSuccess(`Intervalle de synchronisation mis à jour: ${newInterval} heure(s)`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoSync = async (enabled: boolean) => {
    if (!settings) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await syncSettingsAPI.updateSettings({
        auto_sync_enabled: enabled,
      });

      setSettings({
        ...settings,
        auto_sync_enabled: enabled,
      });

      setSuccess(
        enabled
          ? '✅ Synchronisation automatique activée'
          : '⏸️ Synchronisation automatique désactivée'
      );
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatLastSyncTime = (time: string | null) => {
    if (!time) return 'Jamais';
    const date = new Date(time);
    return date.toLocaleString('fr-FR');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700">Impossible de charger les paramètres</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Succès */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Activation/Désactivation */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Synchronisation Automatique
        </h3>

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-medium text-gray-700">État</p>
            <p className="text-sm text-gray-500 mt-1">
              {settings.auto_sync_enabled
                ? 'La synchronisation automatique est activée'
                : 'La synchronisation automatique est désactivée'}
            </p>
          </div>
          <button
            onClick={() => handleToggleAutoSync(!settings.auto_sync_enabled)}
            disabled={saving}
            className={`py-2 px-6 rounded-lg font-medium transition ${
              settings.auto_sync_enabled
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            } disabled:opacity-50`}
          >
            {settings.auto_sync_enabled ? '🟢 Activée' : '⚪ Désactivée'}
          </button>
        </div>

        {settings.auto_sync_enabled && (
          <div className="space-y-4 pt-4 border-t">
            {/* Intervalle */}
            <div>
              <label className="block text-gray-700 font-medium mb-3">
                Intervalle de synchronisation
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Actuellement: toutes les <strong>{settings.sync_interval_hours} heure(s)</strong>
              </p>

              <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                {intervalOptions.map((interval) => (
                  <button
                    key={interval}
                    onClick={() => handleIntervalChange(interval)}
                    disabled={saving}
                    className={`py-2 px-3 rounded-lg font-medium transition ${
                      settings.sync_interval_hours === interval
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } disabled:opacity-50`}
                  >
                    {interval}h
                  </button>
                ))}
              </div>

              <p className="text-xs text-gray-500 mt-3 italic">
                💡 La synchronisation s'exécute automatiquement à l'intervalle sélectionné,
                tant que le serveur est actif.
              </p>
            </div>

            {/* Dernière synchronisation */}
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600">
                <strong>Dernière synchronisation automatique:</strong>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {formatLastSyncTime(settings.last_auto_sync_time)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Informations */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Comment ça fonctionne</h4>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>✓ Le serveur vérifie toutes les minutes quels utilisateurs ont besoin d'une sync</li>
          <li>✓ Si l'intervalle est écoulé, une synchronisation automatique s'exécute</li>
          <li>✓ Les données Dexcom sont importées et les hypos/hypers sont détectées</li>
          <li>✓ Vous pouvez toujours lancer une synchronisation manuelle en cliquant sur le bouton</li>
        </ul>
      </div>

      {/* Avertissement */}
      {!settings.auto_sync_enabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Synchronisation désactivée</h4>
          <p className="text-sm text-yellow-800">
            Vous devrez synchroniser manuellement vos données Dexcom en cliquant sur le bouton
            "Synchroniser" à chaque fois.
          </p>
        </div>
      )}
    </div>
  );
};
